import React, { useState, useEffect } from 'react';
import {
    CreditCard, Check, AlertCircle, ArrowUpCircle, Clock, CheckCircle2,
    XCircle, ChevronDown, ChevronUp, X, Send, Loader, BarChart3
} from 'lucide-react';
import { getSubscriptionPlans } from '../../../../services/api/companyApi';
import { getCurrentTenant } from '../../../../services/api/settingsApi';
import {
    submitUpgradeRequest,
    getMyUpgradeRequests,
} from '../../../../services/api/upgradeRequestApi';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PLAN_ORDER = { BASIC: 1, STANDARD: 2, PROFESSIONAL: 3, ENTERPRISE: 4 };

const planConfig = (planKey, priceAed) => ({
    BASIC:        { name: 'Basic',        price: priceAed,  color: 'from-slate-400 to-slate-600',    border: 'border-slate-200',  badge: 'bg-slate-100 text-slate-700',   popular: false },
    STANDARD:     { name: 'Standard',     price: priceAed,  color: 'from-blue-500 to-indigo-600',    border: 'border-blue-200',   badge: 'bg-blue-100 text-blue-700',     popular: true  },
    PROFESSIONAL: { name: 'Professional', price: priceAed,  color: 'from-purple-600 to-pink-600',   border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', popular: false },
    ENTERPRISE:   { name: 'Enterprise',   price: priceAed,  color: 'from-amber-500 to-orange-600',  border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-700',   popular: false },
}[planKey] || { name: planKey, price: null, color: 'from-gray-400 to-gray-600', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-700', popular: false });

const StatusBadge = ({ status }) => {
    const map = {
        PENDING:  { icon: Clock,       cls: 'bg-amber-100 text-amber-800 border-amber-200',  label: 'Pending Review' },
        APPROVED: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Approved' },
        REJECTED: { icon: XCircle,     cls: 'bg-rose-100 text-rose-800 border-rose-200',    label: 'Rejected' },
    };
    const cfg = map[status] || map.PENDING;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border ${cfg.cls}`}>
            <Icon size={10} /> {cfg.label}
        </span>
    );
};

const generateFeatures = (planKey, details) => {
    const features = [];
    if (details) {
        features.push(details.maxProducts === -1 ? 'Unlimited products' : `Up to ${details.maxProducts?.toLocaleString()} products`);
        features.push(details.maxOrders === -1 ? 'Unlimited orders/month' : `Up to ${details.maxOrders?.toLocaleString()} orders/month`);
        features.push(details.maxUsers === -1 ? 'Unlimited users' : `Up to ${details.maxUsers} users`);
        if (details.isolationStrategy === 'DEDICATED_SCHEMA') features.push('Dedicated database schema');
    }
    if (planKey === 'BASIC')        features.push('Basic dashboard', 'Email support');
    if (planKey === 'STANDARD')     features.push('Full analytics', 'Priority support');
    if (planKey === 'PROFESSIONAL') features.push('Full analytics', 'API access', 'Priority support');
    if (planKey === 'ENTERPRISE')   features.push('Custom analytics', '24/7 support', 'Dedicated manager');
    return features;
};

// ─── Upgrade Request Modal ────────────────────────────────────────────────────

const UpgradeModal = ({ fromPlan, toPlan, toPlanConfig, onClose, onSubmit, submitting }) => {
    const [notes, setNotes] = useState('');
    const cfg = toPlanConfig;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--color-bg-card)] rounded-2xl shadow-2xl w-full max-w-md border border-[var(--color-border)] overflow-hidden">
                {/* Header */}
                <div className={`bg-gradient-to-r ${cfg.color} p-5 text-white`}>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <ArrowUpCircle size={20} />
                                <span className="text-sm font-bold opacity-90">Plan Upgrade Request</span>
                            </div>
                            <h3 className="text-xl font-black">Upgrade to {cfg.name}</h3>
                            <p className="text-sm opacity-80 mt-0.5">
                                {fromPlan} → {cfg.name}
                                {cfg.price != null ? ` · AED ${cfg.price}/mo` : ' · Custom pricing'}
                            </p>
                        </div>
                        <button onClick={onClose} className="opacity-70 hover:opacity-100 transition-opacity">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
                        <Clock size={15} className="text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800 font-medium">
                            Your request will be reviewed by our admin team. You'll see the status updated here once processed.
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-[var(--color-text-primary)] mb-1.5">
                            Notes for Admin <span className="text-[var(--color-text-muted)] font-normal">(optional)</span>
                        </label>
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Tell us why you'd like to upgrade or any specific requirements..."
                            className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-xl bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                        />
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 py-2.5 rounded-xl font-bold text-sm bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] transition-all disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => onSubmit(notes)}
                            disabled={submitting}
                            className={`flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r ${cfg.color} hover:opacity-90 transition-all shadow-md disabled:opacity-60 flex items-center justify-center gap-2`}
                        >
                            {submitting ? <><Loader size={14} className="animate-spin" /> Submitting…</> : <><Send size={14} /> Submit Request</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Request History ──────────────────────────────────────────────────────────

const RequestHistory = ({ requests }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="mt-3 border border-[var(--color-border)] rounded-2xl overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[var(--color-bg-hover)] hover:bg-[var(--color-border-soft)] transition-colors text-left"
            >
                <span className="text-xs font-bold text-[var(--color-text-primary)]">Upgrade Request History ({requests.length})</span>
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {open && (
                <div className="divide-y divide-[var(--color-border-soft)]">
                    {requests.map(r => {
                        const toCfg = planConfig(r.requestedPlan);
                        return (
                            <div key={r.publicId} className="px-4 py-3 bg-[var(--color-bg-card)]">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="text-xs font-bold text-[var(--color-text-primary)]">
                                        {r.currentPlan} → <span className={`bg-gradient-to-r ${toCfg.color} bg-clip-text text-transparent`}>{r.requestedPlan}</span>
                                    </span>
                                    <StatusBadge status={r.status} />
                                </div>
                                <p className="text-[10px] text-[var(--color-text-muted)]">
                                    {new Date(r.createdAt).toLocaleDateString('en-AE', { year: 'numeric', month: 'short', day: 'numeric' })}
                                </p>
                                {r.adminNotes && (
                                    <p className="text-[11px] text-[var(--color-text-secondary)] mt-1 italic">
                                        Admin: "{r.adminNotes}"
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ManageSubscription = () => {
    const [currentPlan, setCurrentPlan] = useState('STANDARD');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [pendingRequest, setPendingRequest] = useState(null);

    // Modal state
    const [upgradeTarget, setUpgradeTarget] = useState(null); // { id, config }
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [plansResponse, tenantResponse, requestsResponse] = await Promise.all([
                getSubscriptionPlans(),
                getCurrentTenant(),
                getMyUpgradeRequests().catch(() => []),
            ]);

            const planDetails = plansResponse.details || {};
            const formatted = Object.entries(planDetails).map(([key, details]) => {
                const cfg = planConfig(key, details.priceAed ?? null);
                return { id: key, ...cfg, features: generateFeatures(key, details), details };
            }).sort((a, b) => (PLAN_ORDER[a.id] ?? 9) - (PLAN_ORDER[b.id] ?? 9));

            setPlans(formatted);
            if (tenantResponse?.subscriptionPlan) setCurrentPlan(tenantResponse.subscriptionPlan);

            setUpgradeRequests(requestsResponse);
            setPendingRequest(requestsResponse.find(r => r.status === 'PENDING') ?? null);
        } catch (err) {
            console.error('ManageSubscription load error:', err);
            setError('Failed to load subscription data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitRequest = async (notes) => {
        setSubmitting(true);
        try {
            await submitUpgradeRequest(upgradeTarget.id, notes);
            setUpgradeTarget(null);
            setSuccessMessage(`Upgrade request to ${upgradeTarget.config.name} submitted! Pending admin review.`);
            setTimeout(() => setSuccessMessage(null), 5000);
            await loadAll(); // refresh to show pending banner
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to submit request.';
            setUpgradeTarget(null); // Ensure modal closes so error is visible!
            setError(msg);
            setTimeout(() => setError(null), 8000);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <LoadingSpinner size="lg" message="Loading subscription plans..." />
            </div>
        );
    }

    const currentPlanIdx = PLAN_ORDER[currentPlan] ?? 0;

    return (
        <div className="h-full flex flex-col p-3 md:p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex-shrink-0 mb-4">
                <h2 className="text-xl font-black text-[var(--color-text-primary)] flex items-center gap-2">
                    <CreditCard size={19} className="text-blue-500" />
                    Manage Subscription
                </h2>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">Choose the plan that fits your business</p>
            </div>

            {/* Feedback banners */}
            {successMessage && (
                <div className="flex-shrink-0 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl mb-4 text-sm font-medium">
                    <CheckCircle2 size={15} className="shrink-0 text-emerald-500" /> {successMessage}
                </div>
            )}
            {error && (
                <div className="flex-shrink-0 flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl mb-4 text-sm font-medium">
                    <AlertCircle size={15} className="shrink-0 text-rose-500" /> {error}
                </div>
            )}

            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-4">
                {/* Pending Request Banner */}
                {pendingRequest && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3.5 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <Clock size={16} className="text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-amber-900">
                                Upgrade request pending — <span className="text-amber-700">{pendingRequest.currentPlan} → {pendingRequest.requestedPlan}</span>
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">
                                Submitted {new Date(pendingRequest.createdAt).toLocaleDateString('en-AE', { month: 'short', day: 'numeric', year: 'numeric' })} · Awaiting admin approval
                            </p>
                        </div>
                        <StatusBadge status="PENDING" />
                    </div>
                )}

                {/* Current Plan */}
                {plans.find(p => p.id === currentPlan) && (() => {
                    const cp = plans.find(p => p.id === currentPlan);
                    return (
                        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-widest">Current Plan</p>
                                    <p className={`text-xl font-black bg-gradient-to-r ${cp.color} bg-clip-text text-transparent mt-0.5`}>
                                        {cp.name}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-[var(--color-text-muted)]">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-[10px]">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" /> Active
                                        </span>
                                        {cp.price != null
                                            ? <span className="font-semibold">AED {cp.price}/month</span>
                                            : <span className="font-semibold">Custom pricing</span>
                                        }
                                        {cp.details && (
                                            <>
                                                <span>{cp.details.maxProducts === -1 ? '∞' : cp.details.maxProducts} products</span>
                                                <span>{cp.details.maxUsers === -1 ? '∞' : cp.details.maxUsers} users</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <CreditCard size={28} className="text-[var(--color-text-muted)] opacity-20 shrink-0" />
                            </div>
                        </div>
                    );
                })()}

                {/* Plan Cards */}
                <div>
                    <h3 className="text-sm font-bold text-[var(--color-text-primary)] mb-3">Available Plans</h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {plans.map((plan) => {
                            const isCurrent = plan.id === currentPlan;
                            const isUpgrade = (PLAN_ORDER[plan.id] ?? 99) > currentPlanIdx;
                            const isDisabledByPending = !!pendingRequest && !isCurrent;

                            return (
                                <div
                                    key={plan.id}
                                    className={`relative bg-[var(--color-bg-card)] rounded-2xl border-2 transition-all duration-200 hover:shadow-md ${isCurrent ? plan.border : 'border-[var(--color-border)]'} ${plan.popular && !isCurrent ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                                            <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full text-white bg-gradient-to-r ${plan.color} shadow`}>
                                                Popular
                                            </span>
                                        </div>
                                    )}
                                    <div className="p-3.5">
                                        <div className="text-center mb-3">
                                            <h4 className={`text-sm font-black bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>{plan.name}</h4>
                                            <div className="mt-0.5">
                                                {plan.price != null
                                                    ? <><span className="text-lg font-black text-[var(--color-text-primary)]">{plan.price}</span><span className="text-[10px] text-[var(--color-text-muted)]"> AED/mo</span></>
                                                    : <span className="text-xs font-bold text-[var(--color-text-secondary)]">Contact Us</span>
                                                }
                                            </div>
                                        </div>

                                        {/* Analytics indicator */}
                                        {(plan.id !== 'BASIC') && (
                                            <div className="flex items-center gap-1 mb-2 text-[10px] text-blue-600 font-bold">
                                                <BarChart3 size={10} /> Analytics included
                                            </div>
                                        )}

                                        <ul className="space-y-1 mb-3 min-h-[90px]">
                                            {plan.features.slice(0, 4).map((f, i) => (
                                                <li key={i} className="flex items-start gap-1.5">
                                                    <Check size={10} className="text-emerald-500 mt-0.5 shrink-0" />
                                                    <span className="text-[10px] text-[var(--color-text-muted)] line-clamp-1">{f}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {isCurrent ? (
                                            <div className={`w-full py-1.5 rounded-xl text-center text-xs font-bold ${plan.badge}`}>
                                                Current Plan
                                            </div>
                                        ) : isDisabledByPending ? (
                                            <button disabled className="w-full py-1.5 rounded-xl text-xs font-bold bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] cursor-not-allowed">
                                                Request Pending
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => setUpgradeTarget({ id: plan.id, config: plan })}
                                                className={`w-full py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r ${plan.color} hover:opacity-90 hover:shadow-md transition-all`}
                                            >
                                                {isUpgrade ? '↑ Request Upgrade' : 'Request Change'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Request History */}
                {upgradeRequests.length > 0 && <RequestHistory requests={upgradeRequests} />}
            </div>

            {/* Upgrade Request Modal */}
            {upgradeTarget && (
                <UpgradeModal
                    fromPlan={currentPlan}
                    toPlan={upgradeTarget.id}
                    toPlanConfig={upgradeTarget.config}
                    onClose={() => setUpgradeTarget(null)}
                    onSubmit={handleSubmitRequest}
                    submitting={submitting}
                />
            )}
        </div>
    );
};

export default ManageSubscription;
